// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

/* eslint-disable prefer-rest-params */

import { Deal } from '@interfaces/deal.interfaces';
import { Assignees, DdjDocument, DocumentData, FieldTypes, ImpactPrinterFields, LineDetail, PddjPrinters, PdfField } from '@interfaces/document.interfaces';
import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { bufferArrayToBase64, isMobileOrTablet } from './utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const applyFields = async (
    pdfTron: WebViewerInstance | any,
    assignee?: Assignees,
    isDesktop?: boolean,
    signedPdfFields?: [{ [key: string]: PdfField }]
): Promise<WebViewerInstance> => {
    const { Annotations, documentViewer } = pdfTron.Core;
    const annotManager = documentViewer.getAnnotationManager();
    const fieldManager = annotManager.getFieldManager();
    const annotationsList = annotManager.getAnnotationsList();
    const annotsToDelete = [];
    const annotsToAdd = [];
    const annotsToDraw = [];

    await Promise.all(
        annotationsList.map(async (annot, index) => {
            let inputAnnot;
            let field;
            let createInnerElement;
            let createSignHereElement;
            const fieldPreviouslySigned = false;

            if (annot?.custom) {
                // create a form field based on the type of annotation
                const widgetName = `widget_${annot.custom.name}`;
                switch (annot.custom?.type) {
                    case FieldTypes.text:
                    case FieldTypes.optionalText:
                    case FieldTypes.requiredText:
                        //Check to see if the field has been signed yet
                        if (hasSignedField(widgetName, signedPdfFields)) {
                            break;
                        }

                        field = new Annotations.Forms.Field(widgetName, {
                            type: 'Tx',
                            value: annot.custom.value,
                            font: {
                                size: 12,
                                name: 'Courier New Bold',
                                strokeColor: new Annotations.Color(0, 0, 0, 1),
                                fillColor: new Annotations.Color(0, 0, 0, 1),
                            },
                        });
                        inputAnnot = new Annotations.TextWidgetAnnotation(field);
                        break;
                    case FieldTypes.optionalInitials:
                    case FieldTypes.optionalLegalName:
                    case FieldTypes.optionalSignature:
                        //Check to see if the field has been signed yet
                        if (hasSignedField(widgetName, signedPdfFields)) {
                            break;
                        }
                        field = new Annotations.Forms.Field(widgetName, {
                            type: 'Sig',
                        });
                        inputAnnot = new Annotations.SignatureWidgetAnnotation(field, {
                            appearance: '_DEFAULT',
                            appearances: {
                                _DEFAULT: {
                                    Normal: {
                                        data: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAYdEVYdFNvZnR3YXJlAHBhaW50Lm5ldCA0LjEuMWMqnEsAAAANSURBVBhXY/j//z8DAAj8Av6IXwbgAAAAAElFTkSuQmCC',
                                        offset: {
                                            x: 100,
                                            y: 100,
                                        },
                                    },
                                },
                            },
                        });

                        createSignHereElement = Annotations.SignatureWidgetAnnotation.prototype.createSignHereElement;
                        inputAnnot.createSignHereElement = function () {
                            // signHereElement is the default one with dark blue background
                            const signHereElement = createSignHereElement.apply(this, arguments);
                            signHereElement.style.cursor = 'pointer';
                            signHereElement.classList.add('optional-sign-here');
                            signHereElement.innerText = 'Opt In';
                            return signHereElement;
                        };
                        break;
                    case FieldTypes.signature:
                    case FieldTypes.initials:
                    case FieldTypes.legalName:
                        //Check to see if the field has been signed yet
                        if (hasSignedField(widgetName, signedPdfFields)) {
                            break;
                        }
                        field = new Annotations.Forms.Field(widgetName, {
                            type: 'Sig',
                        });
                        inputAnnot = new Annotations.SignatureWidgetAnnotation(field, {
                            appearance: '_DEFAULT',
                            appearances: {
                                _DEFAULT: {
                                    Normal: {
                                        data: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAYdEVYdFNvZnR3YXJlAHBhaW50Lm5ldCA0LjEuMWMqnEsAAAANSURBVBhXY/j//z8DAAj8Av6IXwbgAAAAAElFTkSuQmCC',
                                        offset: {
                                            x: 100,
                                            y: 100,
                                        },
                                    },
                                },
                            },
                        });

                        createSignHereElement = Annotations.SignatureWidgetAnnotation.prototype.createSignHereElement;
                        inputAnnot.createSignHereElement = function () {
                            // signHereElement is the default one with dark blue background
                            const signHereElement = createSignHereElement.apply(this, arguments);
                            signHereElement.style.cursor = 'pointer';
                            signHereElement.classList.add('sign-here');
                            return signHereElement;
                        };
                        break;
                    case FieldTypes.date:
                        //Check to see if the field has been signed yet
                        if (hasSignedField(widgetName, signedPdfFields)) {
                            break;
                        }
                        field = new Annotations.Forms.Field(`widget_${annot.custom.name}`, {
                            type: 'Tx',
                            font: {
                                size: 12,
                            },
                        });
                        //Datefields now handled by html5
                        inputAnnot = new Annotations.TextWidgetAnnotation(field);
                        break;
                    case FieldTypes.vin:
                    case FieldTypes.last8Vin:
                    case FieldTypes.vehicleYear:
                    case FieldTypes.vehicleMake:
                    case FieldTypes.vehicleModel:
                    case FieldTypes.vehicleTrim:
                    case FieldTypes.dealNumber:
                    case FieldTypes.dealershipName:
                    case FieldTypes.listOfDocuments:
                    case FieldTypes.dealerContact:
                    case FieldTypes.buyerName:
                    case FieldTypes.cobuyerName:
                    case FieldTypes.tradeInVehicleYear:
                    case FieldTypes.tradeInVehicleMake:
                    case FieldTypes.tradeInVehicleModel:
                    case FieldTypes.tradeInVehicleTrim:
                    case FieldTypes.tradeInVehiclePayoff:
                    case FieldTypes.jobTitle:
                    case FieldTypes.tradeInVin:
                    case FieldTypes.stockId:
                    case FieldTypes.mileage:
                        // These fields will be handled elsewhere since they will become readonly fields
                        inputAnnot = undefined;
                        break;
                    default: // prevent duplicates when importing xfdf
                        inputAnnot = undefined;
                        annotManager.deleteAnnotation(annot, false, true);
                        break;
                }

                if (inputAnnot) {
                    if (
                        annot.custom?.type !== FieldTypes.signature &&
                        annot.custom?.type !== FieldTypes.initials &&
                        annot.custom?.type !== FieldTypes.legalName &&
                        annot.custom?.type !== FieldTypes.optionalInitials &&
                        annot.custom?.type !== FieldTypes.optionalLegalName &&
                        annot.custom?.type !== FieldTypes.optionalSignature
                    ) {
                        createInnerElement = getCreateInnerElement(pdfTron, inputAnnot.fieldName);
                        inputAnnot.createInnerElement = function () {
                            const el = createInnerElement.apply(this, arguments);
                            el.classList.add('text-field');
                            el.classList.add(`text-field_${annot.custom?.type}`);

                            if (annot.custom?.type === FieldTypes.date) {
                                const changeEvent = function (event) {
                                    let value = event.target.value;
                                    event.target.setAttribute('type', 'text');
                                    event.target.setAttribute('placeholder', '');
                                    if (!event.target.value.includes('/')) {
                                        field.setValue(`${value.substring(5, 7)}/${value.substring(8, 10)}/${value.substring(0, 4)}`);
                                    } else {
                                        value = value.replace(/\D/g, '');
                                        if (value.length !== 8) {
                                            field.setValue(undefined);
                                            event.target.setAttribute('placeholder', 'Select Date');
                                        }
                                    }
                                };

                                // Mobile Safari only
                                // const ua = window.navigator.userAgent;
                                // const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
                                // const webkit = !!ua.match(/WebKit/i);
                                // const iOSSafari = iOS && webkit && !ua.match(/CriOS/i);

                                const shouldApplyMobileCalendar = isDesktop === undefined ? isMobileOrTablet() : !isDesktop;
                                if (shouldApplyMobileCalendar) {
                                    setTimeout(() => {
                                        el.setAttribute('type', 'date');
                                        el.setAttribute('placeholder', '');
                                    }, 1000);
                                } else {
                                    el.setAttribute('type', 'text');
                                    el.setAttribute('placeholder', 'Select Date');
                                    el.addEventListener('click', function (event) {
                                        event.target.setAttribute('type', 'date');
                                        field.setValue('');
                                    });
                                    el.addEventListener('change', changeEvent);
                                    el.addEventListener('keypress', function (event) {
                                        const value = event.target.value.replace(/\D/g, '');
                                        // 45: -, 47: /, 48-57: numbers
                                        if (value.length + 1 > 8 || (event.which !== 45 && (event.which < 47 || event.which > 57))) {
                                            event.preventDefault();
                                        }
                                    });
                                    el.addEventListener('blur', function (event) {
                                        el.setAttribute('type', 'text');
                                        if (event.target.value === '') {
                                            el.setAttribute('placeholder', 'Select Date');
                                        }

                                        if (!el.value.includes('/') && el.value) {
                                            field.setValue(`${el.value.substring(5, 7)}/${el.value.substring(8, 10)}/${el.value.substring(0, 4)}`);
                                        } else {
                                            el.value = el.value.replace(/\D/g, '');
                                            // If the date value is greater than 8 digits it's invalid, so we just remove
                                            if (el.value.length !== 8) {
                                                field.setValue(undefined);
                                            }
                                        }
                                    });
                                }
                            }

                            return el;
                        };
                    }
                    // set position
                    inputAnnot.PageNumber = annot.getPageNumber();
                    inputAnnot.X = annot.getX();
                    inputAnnot.Y = annot.getY();
                    inputAnnot.rotation = annot.Rotation;
                    if (annot.Rotation === 0 || annot.Rotation === 180) {
                        inputAnnot.Width = annot.getWidth();
                        if (annot.custom?.type === FieldTypes.signature && annot.getHeight() <= fieldHeightByType(FieldTypes.signature)) {
                            inputAnnot.Height = annot.getHeight() * 2;
                            inputAnnot.Y -= annot.getHeight() / 2;
                        } else {
                            inputAnnot.Height = annot.getHeight();
                        }
                    } else {
                        inputAnnot.Width = annot.getHeight();
                        inputAnnot.Height = annot.getWidth();
                    }

                    // draw the annotation in the viewer based on the assignee(s) we want to see
                    if (!assignee || assignee === annot.custom?.assignee) {
                        // Add the annotation to the document
                        annotManager.addAnnotation(inputAnnot);
                        fieldManager.addField(field);
                        annotsToDraw.push(inputAnnot);
                        // delete original annotation
                        annotsToDelete.push(annot);
                        //annotManager.deleteAnnotation(annot, false, true);
                    }
                }
            }
        })
    );

    // delete old annotations
    annotManager.deleteAnnotations(annotsToDelete, null);

    // refresh viewer
    await annotManager.drawAnnotationsFromList(annotsToDraw);
    return pdfTron;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const applyImpactFields = async (
    pdfTron: WebViewerInstance | any,
    impactData: ImpactPrinterFields,
    document?: DdjDocument
): Promise<WebViewerInstance> => {
    const { Annotations, documentViewer } = pdfTron.Core;
    const annotManager = documentViewer.getAnnotationManager();
    const fieldManager = annotManager.getFieldManager();
    const { WidgetFlags } = Annotations;
    const annotsToDraw = [];
    const settings = getImpactSettings(document, impactData);
    const flags = new WidgetFlags();
    flags.set('ReadOnly', true);

    // Handle PNG impact data coming over inside a Base64 string
    if (impactData.rawBase64) {
        const pageSettings = impactData?.MetaDataPrinter?.advancedPageSettings;

        const annot = new Annotations.StampAnnotation({
            PageNumber: 1,
            // Current offset calculations are coming over as negative.
            // Instead of messing with the regular offset handling we
            // just double negative these offsets for PNG impacts.
            // If they give us a starting point then try to use that to initially place the data
            X: (pageSettings?.PageBoundryX || 0) + (-document.offsetX || 0),
            Y: (pageSettings?.PageBoundryY || 0) + (-document.offsetY || 0),
            // Default to letter size. The defaults returned by API should be the same.
            // ?? should proceed to the next option if it is 0
            Height: pageSettings?.PageBoundryY2 - pageSettings?.PageBoundryY ?? pageSettings?.PageHeight ?? 792,
            Width: pageSettings?.PageBoundryX2 - pageSettings?.PageBoundryX ?? pageSettings?.PageWidth ?? 612,
            ReadOnly: true,
            NoResize: true,
        });

        annot.setImageData(impactData.rawBase64);

        annotManager.deselectAllAnnotations();
        annotManager.addAnnotation(annot);
        annotManager.redrawAnnotation(annot);
        annotsToDraw.push(annot);
        // Force readonly data to the back of the annotation stack
        annotManager.bringToBack(annot);
    } else if (impactData.DocumentDataLines && impactData.DocumentDataLines.length > 0) {
        let height = 0;
        let startFirstSpacing = false;
        impactData.DocumentDataLines.forEach((line, index) => {
            if (line.LineData.length > 0 && startFirstSpacing === false) {
                startFirstSpacing = true;
            }

            let lineHeight = 0;
            lineHeight = parseFloat(line.LineFeedVerticalSpacing);

            // Set the new height of the current line
            if (index > 0 && startFirstSpacing) {
                height += lineHeight;
            }

            // Loop over lineData items and create a field for each
            line.LineData.forEach((data, xIndex) => {
                // Don't bother righting empty fields
                if (data.Content.trim() === '') {
                    return;
                }

                if (startFirstSpacing) {
                    const field = new Annotations.Forms.Field(`line_${index}_data_${xIndex}`, {
                        type: 'Tx',
                        value: data.Content,
                        flags,
                        font: {
                            name: settings.fontFamily,
                            size: settings.fontSize,
                        },
                    });

                    const widgetAnnot = new Annotations.TextWidgetAnnotation(field);
                    widgetAnnot.PageNumber = 1;
                    // Assuming each space is one character
                    widgetAnnot.X = parseFloat(data.Spacing) * settings.spacingAdjustment - settings.horizontalAdjustment;
                    widgetAnnot.Y = height - settings.verticalAdjustment;
                    // Each character appears to be 10 units wide
                    const recalc = data.Content.length > 0 ? data.Content.replace(/\s/g, '').length : 0;
                    const calcWidth = recalc * settings.characterAdjustment + (data.Content.length - recalc) * settings.spacingAdjustment;
                    widgetAnnot.Width = calcWidth < 575 ? calcWidth : 575 - (document.offsetX || 0);
                    widgetAnnot.Height = settings.standardLineHeight;

                    // If the actual content goes to the end of the string, but the calculated width does not
                    // fit the actual content we default to the content's actual width
                    if (data.Content.length * settings.characterAdjustment > widgetAnnot.Width && data.Content.slice(-1) !== ' ') {
                        widgetAnnot.Width = data.Content.length * settings.characterAdjustment;
                    }

                    fieldManager.addField(field);
                    annotManager.addAnnotation(widgetAnnot);
                    annotsToDraw.push(widgetAnnot);
                    // Force readonly data to the back of the annotation stack
                    annotManager.bringToBack(widgetAnnot);
                }
            });
        });
    }

    await annotManager.drawAnnotationsFromList(annotsToDraw);

    return pdfTron;
};

export const applyImpactFieldsForXandYTesting = async (
    pdfTron: WebViewerInstance | any,
    impactData: ImpactPrinterFields,
    document?: DdjDocument
): Promise<WebViewerInstance> => {
    const { Annotations, documentViewer } = pdfTron.Core;
    const annotManager = documentViewer.getAnnotationManager();
    const annotsToDraw = [];
    const settings = getImpactSettings(document, impactData);
    const { WidgetFlags } = Annotations;
    const flags = new WidgetFlags();
    flags.set('ReadOnly', true);
    let inputAnnot;

    if (impactData.rawBase64) {
        const options = {
            PageNumber: 1,
            // Current offset calculations are coming over as negative.
            // Instead of messing with the regular offset handling we
            // just double negative these offsets for PNG impacts
            X: -document.offsetX || 0,
            Y: -document.offsetY || 0,
            // Default to letter size. The defaults returned by API should be the same.
            Height: impactData?.MetaDataPrinter?.advancedPageSettings?.PageHeight || 792,
            Width: impactData?.MetaDataPrinter?.advancedPageSettings?.PageWidth || 612,
            NoResize: true,
        };

        const annot = new Annotations.StampAnnotation(options);

        annot.setImageData(impactData.rawBase64);

        annotManager.deselectAllAnnotations();
        annotManager.addAnnotation(annot);
        annotManager.redrawAnnotation(annot);
        annotsToDraw.push(annot);
        // Force readonly data to the back of the annotation stack
        annotManager.bringToBack(annot);
    } else if (impactData.DocumentDataLines && impactData.DocumentDataLines.length > 0) {
        let height = 0;
        let startFirstSpacing = false;
        impactData.DocumentDataLines.forEach((line, index) => {
            if (line.LineData.length > 0 && startFirstSpacing === false) {
                startFirstSpacing = true;
            }

            let lineHeight = 0;
            lineHeight = parseFloat(line.LineFeedVerticalSpacing);

            if (index > 0 && startFirstSpacing) {
                height += lineHeight;
            }

            line.LineData.forEach((data, xIndex) => {
                if (data.Content.trim() === '') {
                    return;
                }

                if (startFirstSpacing) {
                    const freeText = new Annotations.FreeTextAnnotation();
                    freeText.LockedContents = true;
                    freeText.setPadding(new Annotations.Rect(0, 0, 0, 0));
                    freeText.setContents(data.Content);
                    freeText.FillColor = new Annotations.Color(255, 255, 255, 0);
                    freeText.FontSize = `${settings.fontSize.toString()}pt`;
                    freeText.Font = settings.fontFamily;
                    freeText.TextColor = new Annotations.Color(0, 0, 0);
                    freeText.disableRotationControl();
                    freeText.StrokeColor = new Annotations.Color(255, 255, 255, 0);
                    freeText.StrokeThickness = 0;
                    freeText.NoResize = true;

                    inputAnnot = freeText;
                    inputAnnot.PageNumber = inputAnnot.getPageNumber();
                    inputAnnot.X = parseFloat(data.Spacing) * settings.spacingAdjustment - settings.horizontalAdjustment;
                    inputAnnot.Y = height - settings.verticalAdjustment;
                    // Each character appears to be 10 units wide
                    const recalc = data.Content.length > 0 ? data.Content.replace(/\s/g, '').length : 0;
                    const calcWidth = recalc * settings.characterAdjustment + (data.Content.length - recalc) * settings.spacingAdjustment;
                    inputAnnot.Width = calcWidth < 575 ? calcWidth : 575 - (document.offsetX || 0);
                    inputAnnot.Height = settings.standardLineHeight;

                    // If the actual content goes to the end of the string, but the calculated width does not
                    // fit the actual content we default to the content's actual width
                    if (data.Content.length * settings.characterAdjustment > inputAnnot.Width && data.Content.slice(-1) !== ' ') {
                        inputAnnot.Width = data.Content.length * settings.characterAdjustment;
                    }

                    annotManager.addAnnotation(inputAnnot);
                    annotsToDraw.push(inputAnnot);
                }
            });
        });
    }
    // refresh viewer
    await annotManager.drawAnnotationsFromList(annotsToDraw);
    return pdfTron;
};

export const applyReadOnlyFields = async (
    pdfTron: WebViewerInstance | any,
    deal?: Deal,
    testing = false
): Promise<{ update: WebViewerInstance; updatePitsFields: { [key: string]: PdfField } }> => {
    const { Annotations, documentViewer } = pdfTron.Core;
    const annotManager = documentViewer.getAnnotationManager();
    const fieldManager = annotManager.getFieldManager();
    const annotationsList = annotManager.getAnnotationsList();
    const { WidgetFlags } = Annotations;
    const annotsToDelete = [];
    const annotsToDraw = [];
    const flags = new WidgetFlags();
    const updatePitsFields: { [key: string]: PdfField } = {};
    flags.set('ReadOnly', true);

    await Promise.all(
        annotationsList.map(async (annot, index) => {
            // create a read only field based on the type of annotation
            switch (annot.custom?.type) {
                case FieldTypes.vin:
                case FieldTypes.last8Vin:
                case FieldTypes.vehicleYear:
                case FieldTypes.vehicleMake:
                case FieldTypes.vehicleModel:
                case FieldTypes.vehicleTrim:
                case FieldTypes.dealNumber:
                case FieldTypes.dealershipName:
                case FieldTypes.listOfDocuments:
                case FieldTypes.dealerContact:
                case FieldTypes.buyerName:
                case FieldTypes.cobuyerName:
                case FieldTypes.tradeInVehicleYear:
                case FieldTypes.tradeInVehicleMake:
                case FieldTypes.tradeInVehicleModel:
                case FieldTypes.tradeInVehicleTrim:
                case FieldTypes.stockId:
                case FieldTypes.tradeInVin:
                case FieldTypes.mileage:
                case FieldTypes.jobTitle:
                case FieldTypes.tradeInVehiclePayoff: {
                    const value = getReadOnlyFieldValue(annot.custom?.type, deal, testing);

                    const field = new Annotations.Forms.Field(`widget_deal_${annot.custom?.name}`, {
                        type: 'Tx',
                        value,
                        flags,
                        font: {
                            size: 0, //indicates to have a auto font based on the length of the text inside of the text box.
                            name: 'Courier New Bold',
                            strokeColor: new Annotations.Color(0, 0, 0, 1),
                            fillColor: new Annotations.Color(0, 0, 0, 1),
                        },
                    });

                    const widgetAnnot = SetupwidgetAnnotion(field, annot);

                    if (annot.custom?.type === FieldTypes.listOfDocuments && deal?.listOfDocuments) {
                        let iterateListFields = {
                            listX: widgetAnnot.X,
                            listY: widgetAnnot.Y,
                            startingPositionY: widgetAnnot.Y,
                            count: 0,
                        };

                        /*
                            SetupAnnotations(), when called repeatedly like this, creates two columns on the pdf.
                            Each column is only 40 characters wide, which is why we split titles/words at that boundary.
                            If we do not split words at 40 to create multiple annotations for a single doc...
                                - the document titles will look fine when first added (here)
                                - any overflow is hidden
                                - once the user signs, and the annotations are flattend, nothing is hidden anymore, and the font adjusts so all the text can fit.
                            NOTE: different browsers act differently here, test with chrome AND firefox
                        */
                        const allDocs = deal.listOfDocuments;
                        const columnBoundary = 40;
                        allDocs.forEach((documentTitle) => {
                            // split title into words

                            const words = documentTitle.split(' ');
                            // build list of lines
                            const lines = [''];
                            for (const word of words) {
                                // for each word, add it to the previous/current line if possible, otherwise start a new line.

                                const last = lines[lines.length - 1].length ? lines[lines.length - 1] + ' ' : '';
                                if (word.length > columnBoundary) {
                                    // this word is too long, even to be on it's own line.  so add what we can to the previous line, and add the rest to following lines.
                                    const remainingInLast = columnBoundary - last.length;

                                    lines[lines.length - 1] = last + word.slice(0, remainingInLast);
                                    for (
                                        let remainingWordLength = word.length - remainingInLast;
                                        remainingWordLength > 0;
                                        remainingWordLength -= columnBoundary
                                    ) {
                                        const sliceStart = word.length - remainingWordLength;
                                        lines.push(word.slice(sliceStart, sliceStart + columnBoundary));
                                    }
                                } else {
                                    // this word can fit on it's own line.  first check if it can fit in the previous line.
                                    if ((last + word).trim().length <= columnBoundary) {
                                        lines[lines.length - 1] = (last + word).trim();
                                    } else {
                                        // this word starts a new line
                                        lines.push(word);
                                    }
                                }
                            }

                            for (let ndx = 0; ndx < lines.length; ndx++) {
                                const line = lines[ndx];
                                if (line.length) {
                                    const annots = SetupAnnotations(line, annot, iterateListFields, ndx === 0);
                                    iterateListFields = annots?.iterateListFields;

                                    if (annots?.pdfField) {
                                        updatePitsFields[`${annots.pdfField.name}`] = annots.pdfField;
                                    }
                                }
                            }
                        });
                    } else {
                        annotManager.addAnnotation(widgetAnnot);
                        fieldManager.addField(field);
                        annotsToDraw.push(widgetAnnot);
                        // Force readonly data to the back of the annotation stack
                        //annotManager.bringToBack(widgetAnnot); //This seems to delete the fields and only show annotations. 1 field will have field data.
                    }

                    // delete original annotation
                    annotsToDelete.push(annot);
                    break;
                }
                default: // all other fields have been handled already
                    break;
            }
        })
    );

    function SetupwidgetAnnotion(field: any, annot: any, _listX: any = null, _listY: any = null) {
        const widgetAnnot = new Annotations.TextWidgetAnnotation(field);
        widgetAnnot.PageNumber = annot.getPageNumber();
        widgetAnnot.X = _listX ?? annot.getX();
        widgetAnnot.Y = _listY ?? annot.getY();
        widgetAnnot.rotation = annot.Rotation;
        if (annot.Rotation === 0 || annot.Rotation === 180) {
            widgetAnnot.Width = annot.getWidth();
            widgetAnnot.Height = annot.getHeight();
        } else {
            widgetAnnot.Width = annot.getHeight();
            widgetAnnot.Height = annot.getWidth();
        }
        return widgetAnnot;
    }

    function SetupAnnotations(doc: string, annot: any, iterateListFields: any, renderBullet = true): any {
        const docField = new Annotations.Forms.Field(`widget_deal_${annot.custom?.name}_${iterateListFields.count}`, {
            type: 'Tx',
            value: renderBullet ? `\u2022 ${doc}` : ` ${doc}`,
            flags,
            font: {
                size: 0, //indicates to have a auto font based on the length of the text inside of the text box.
                name: 'Courier New Bold',
                strokeColor: new Annotations.Color(0, 0, 0, 1),
                fillColor: new Annotations.Color(0, 0, 0, 1),
            },
        });

        let listOfDealDocumentsType = false;
        if (annot.custom?.name?.includes('LIST_OF_DEAL_DOCUMENTS')) {
            listOfDealDocumentsType = true;
        }

        const widgetAnnotList = SetupwidgetAnnotion(docField, annot, iterateListFields.listX, iterateListFields.listY);

        if (iterateListFields.count == 20) {
            iterateListFields.listX += 250;
            iterateListFields.listY = iterateListFields.startingPositionY;
        } else {
            iterateListFields.listY += 13;
        }

        iterateListFields.count++;
        annotManager.addAnnotation(widgetAnnotList);
        fieldManager.addField(docField);
        annotsToDraw.push(widgetAnnotList);

        if (listOfDealDocumentsType) {
            const currentDate = new Date();
            const pdfField: PdfField = {
                page: widgetAnnotList.getPageNumber(),
                x: widgetAnnotList.X,
                y: widgetAnnotList.Y,
                name: widgetAnnotList.fieldName.toString().split('widget_deal_')[1],
                height: widgetAnnotList.getHeight(),
                value: widgetAnnotList.getField().getValue() ?? '',
                type: FieldTypes.listOfDocuments,
                assignee: Assignees.deal,
                fontSize: 12, //font size is calculated after its been rendered on the page. prepareFieldsForMetaDataSave func will set the final size.
                order: iterateListFields.count,
                width: widgetAnnotList.getWidth(),
                signedBy: '0',
                signedAt: currentDate,
                setAsActive: null,
            };

            return { iterateListFields, pdfField: pdfField };
        }
        return iterateListFields;
    }

    // delete old annotations
    annotManager.deleteAnnotations(annotsToDelete, null);

    await annotManager.drawAnnotationsFromList(annotsToDraw);

    return {
        update: pdfTron,
        updatePitsFields: updatePitsFields,
    };
};

export const applyWatermark = (
    pdfTron: WebViewerInstance,
    text,
    location: 'diagonal' | 'header' | 'footer' = 'diagonal',
    color = 'red',
    fontSize = 25,
    opacity = 100
) => {
    const { documentViewer } = pdfTron.Core;
    const options = {
        [location]: { text, fontSize, color, opacity },
        shouldDrawOverAnnotations: true,
    };
    documentViewer.setWatermark(options);
};

// Goes in and hides fields that do not pertain to the current user
export const handleFieldsVisibility = async (
    pdfTron: WebViewerInstance | any,
    assignee?: Assignees,
    signedPdfFields?: [{ [key: string]: PdfField }],
    activeSigning: boolean = false
) => {
    const { documentViewer, annotationManager } = pdfTron.Core;

    const getFieldNameAndValue = (field) => {
        const { name } = field;

        handlePreviousSigningFields(pdfTron, field, annotationManager, assignee, signedPdfFields);
        // Check children fields
        field.children.forEach(getFieldNameAndValue);
    };

    documentViewer.addEventListener('annotationsLoaded', () => {
        const fieldManager = annotationManager.getFieldManager();
        fieldManager.forEachField(getFieldNameAndValue);
    });
};

export const handlePreviousSigning = async (
    pdfTron: WebViewerInstance | any,
    field: any,
    annotationManager: any,
    assignee?: Assignees,
    signedPdfFields?: [{ [key: string]: PdfField }],
    applyByField: boolean = true
) => {
    handlePreviousSigningFields(pdfTron, field, annotationManager, assignee, signedPdfFields, applyByField);
};

export const handlePreviousSigningWithSignatureMetaData = async (
    pdfTron: WebViewerInstance | any,
    signedMetaData: { [key: string]: PdfField }[],
    assignee?: Assignees
) => {
    const { Annotations } = pdfTron.Core;
    const { annotationManager } = pdfTron.Core;
    const { WidgetFlags } = Annotations;
    const fieldManager = annotationManager.getFieldManager();
    const flags = new WidgetFlags();
    flags.set('ReadOnly', true);
    const font = {
        size: 0, //indicates to have a auto font based on the length of the text inside of the text box.
        name: 'Courier New Bold',
        strokeColor: new Annotations.Color(0, 0, 0, 1),
        fillColor: new Annotations.Color(0, 0, 0, 1),
    };

    Object.keys(signedMetaData).forEach((signingSessionData) => {
        const sessionData: PdfField = signedMetaData[signingSessionData];
        //handlePreviousSigningFields(pdfTron, key, annotationManager, false);
        Object.keys(sessionData).forEach((field) => {
            const fieldObj: PdfField = sessionData[field];
            const isSignatureType = isSignature(fieldObj.type);
            if (!fieldObj.name.includes(`_${assignee}_`)) {
                // Create a stamp to show the field was signed without the signature field being populated

                if (isSignatureType) {
                    const stamp = new Annotations.StampAnnotation();
                    stamp.PageNumber = fieldObj.page;
                    stamp.Height = isSignatureType ? fieldObj.value.height : fieldObj.height;
                    stamp.Width = isSignatureType ? fieldObj.value.width : fieldObj.width;
                    stamp.X = isSignatureType ? fieldObj.value.x : fieldObj.x;
                    stamp.Y = isSignatureType ? fieldObj.value.y : fieldObj.y;
                    stamp.setImageData(fieldObj.value.value);
                    stamp.ReadOnly = true;
                    annotationManager.deselectAllAnnotations();
                    annotationManager.addAnnotation(stamp);
                    annotationManager.redrawAnnotation(stamp);
                } else {
                    let field: any = {};
                    let inputAnnot: any = {};

                    field = new Annotations.Forms.Field(fieldObj.name, {
                        type: 'Tx',
                        value: fieldObj.value,
                        flags,
                        font: font,
                        size: fieldObj.fontSize ?? 12,
                    });

                    inputAnnot = new Annotations.TextWidgetAnnotation(field);
                    inputAnnot.PageNumber = fieldObj.page;
                    inputAnnot.X = fieldObj.x;
                    inputAnnot.Y = fieldObj.y;
                    inputAnnot.Width = fieldObj.width;
                    inputAnnot.Height = fieldObj.height;

                    //const widgetAnnot = SetupwidgetAnnotion(field, annot);

                    annotationManager.addAnnotation(inputAnnot);
                    fieldManager.addField(field);
                    annotationManager.redrawAnnotation(inputAnnot);
                }
            }
        });
    });
};

function handlePreviousSigningFields(
    pdfTron: WebViewerInstance | any,
    field: any,
    annotationManager: any,
    assignee?: Assignees,
    signedPdfFields?: [{ [key: string]: PdfField }],
    applyByField: boolean = true
) {
    const { Annotations } = pdfTron.Core;
    const { WidgetFlags } = Annotations;
    const flags = new WidgetFlags();
    if (applyByField) {
        flags.set('ReadOnly', true);
    }

    const { name } = field;

    const mergedSigningMetaData: { [key: string]: PdfField } = signedPdfFields.reduce((accumulator, current) => {
        // Fields were coming over as both objects and as strings
        // so this is just a precaution
        if (typeof current === 'string') {
            current = JSON.parse(current);
        }

        return { ...accumulator, ...current };
    }, {});

    if (!name.includes(`_${assignee}_`) && !name.includes('_deal_')) {
        if (applyByField) {
            field.set({ flags });
        }

        if (signedPdfFields.length > 0 && mergedSigningMetaData[name.replace('widget_', '')]) {
            const data = mergedSigningMetaData[name.replace('widget_', '')];
            if (isSignature(data.type)) {
                if (applyByField) {
                    field.hide();
                }

                // Create a stamp to show the field was signed without the signature field being populated
                const stamp = new Annotations.StampAnnotation();
                stamp.PageNumber = data.page;
                stamp.Height = data.value.height;
                stamp.Width = data.value.width;
                stamp.X = data.value.x;
                stamp.Y = data.value.y;
                stamp.setImageData(data.value.value);
                stamp.ReadOnly = true;
                annotationManager.deselectAllAnnotations();
                annotationManager.addAnnotation(stamp);
                annotationManager.redrawAnnotation(stamp);
            }
        }
    }
}

export const removeAnnotations = (pdfTron: WebViewerInstance | any, type: 'Free Text' | 'Stamp') => {
    const { annotationManager } = pdfTron.Core;
    const annotationsList = annotationManager.getAnnotationsList();
    const toRemove = [];

    annotationsList.forEach((annot) => {
        if (annot.Subject === type) {
            // Stamp will not be removed if set to ReadOnly
            annot.ReadOnly = false;
            toRemove.push(annot);
        }
    });

    if (toRemove.length > 0) {
        annotationManager.deleteAnnotations(toRemove);
    }
};

export const removeAllAnnotations = async (pdfTron: WebViewerInstance) => {
    const { documentViewer } = pdfTron.Core;
    const annotManager = documentViewer.getAnnotationManager();
    const annotationsList = annotManager.getAnnotationsList();
    const annotsToDelete = [];

    await Promise.all(
        annotationsList.map(async (annot, index) => {
            // Free Text annotations are the initial ones we add when editing documents
            if (annot.Subject === 'Free Text') {
                annotsToDelete.push(annot);
            }
        })
    );

    // delete old annotations
    annotManager.deleteAnnotations(annotsToDelete, null);

    return pdfTron;
};

export const cloneFields = (fields: { [key: string]: PdfField }) => {
    return Object.keys(fields || {}).reduce((obj, fieldName) => {
        obj[fieldName] = { ...fields[fieldName] };
        return obj;
    }, {});
};

export const getDdjField = (name: string, meta: any, assignee: Assignees, type: FieldTypes, value: string | number | Date) => {
    return {
        assignee: assignee,
        fontSize: +parseFloat(meta.FontSize),
        height: meta.Height,
        name: name,
        order: null,
        page: meta.PageNumber,
        signedAt: null,
        signedBy: null,
        type: type,
        width: meta.Width,
        value: value,
        y: meta.Y,
        x: meta.X,
    };
};

export const prepareFileForUpload = async (pdfTron, includeFields = true): Promise<Blob> => {
    if (!pdfTron) {
        return null;
    }
    const { annotManager, documentViewer } = pdfTron.Core;
    const doc = documentViewer.getDocument();
    let xfdfString;
    if (includeFields) {
        xfdfString = await annotManager.exportAnnotations({
            widgets: true,
            fields: true,
        });
    }
    const data = await doc.getFileData({ xfdfString });
    const arr = new Uint8Array(data);
    return new Blob([arr], { type: 'application/pdf' });
};

export const prepareFileForUploadBase64 = async (pdfTron, includeFields = true): Promise<string> => {
    if (!pdfTron) {
        return null;
    }
    const documentViewer: Core.DocumentViewer = pdfTron.Core.documentViewer;
    const annotationManager: Core.AnnotationManager = pdfTron.Core.annotationManager;
    const doc = documentViewer.getDocument();
    let xfdfString;
    if (includeFields) {
        xfdfString = await annotationManager.exportAnnotations({
            widgets: true,
            fields: true,
        });
    }

    const data = await doc.getFileData({ xfdfString });
    const base64Pdf = bufferArrayToBase64(data);
    return base64Pdf;
};

export const getColorsByAssignee = (assignee: Assignees, Colorizer: typeof Core.Annotations.Color): { fill; text; stroke } => {
    const text = new Colorizer(255, 255, 255);
    const stroke = new Colorizer(0, 0, 0);
    switch (assignee) {
        case Assignees.buyer:
            return { fill: new Colorizer(8, 66, 137), text, stroke };
        case Assignees.cobuyer:
            return { fill: new Colorizer(31, 108, 40), text, stroke };
        case Assignees.dealer:
            return { fill: new Colorizer(237, 17, 16), text, stroke };
        case Assignees.deal:
        case Assignees.unk:
            return { fill: new Colorizer(128, 128, 128), text, stroke };
    }
};

export const fieldWidthByType = (type: FieldTypes) => {
    switch (type) {
        case FieldTypes.date:
            return 100;
        case FieldTypes.initials:
            return 60;
        case FieldTypes.vehicleYear:
        case FieldTypes.vehicleMake:
        case FieldTypes.vehicleModel:
        case FieldTypes.last8Vin:
            return 140;
        default:
            return 200;
    }
};

export const fieldHeightByType = (type: FieldTypes) => {
    switch (type) {
        default:
            return 20.0;
    }
};

export const getAssigneeFromName = (annotationName = '') => {
    const split = annotationName.split('_');
    // only look at the beginning
    switch (split[0]) {
        case 'buyer':
            return Assignees.buyer;
        case 'cobuyer':
            return Assignees.cobuyer;
        case 'dealer':
            return Assignees.dealer;
        default:
            return Assignees.deal;
    }
};

export const getTypeFromName = (assignee: Assignees, annotationName = '') => {
    const split = annotationName.split('_');
    if (assignee == Assignees.deal) {
        return split[0] ?? '';
    } else if (split.length >= 2) {
        return split[1];
    } else {
        return '';
    }
};

export const base64ToBlob = (base64: string) => {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; ++i) {
        bytes[i] = binaryString.charCodeAt(i);
    }

    return new Blob([bytes], { type: 'application/pdf' });
};

export const docToBase64Json = async (pdfTron: WebViewerInstance | any, fileName: string): Promise<any> => {
    // anything except cleaning a pdf
    const base64 = await prepareFileForUploadBase64(pdfTron, false);
    if (base64) {
        const docJson = JSON.stringify({ base64File: base64, filename: fileName });
        const fileSize = Math.round(base64.replace(/=/g, '').length * 0.75) ?? 0;

        return { docJson, fileSize };
    }

    return null;
};

export const getReadOnlyFieldValue = (fieldType: FieldTypes, deal?: Deal, testing = false) => {
    switch (fieldType) {
        case FieldTypes.vin:
            return deal?.vehicle?.vin ? deal?.vehicle?.vin : testing ? 'JH4DA3469HS019532' : '';
        case FieldTypes.last8Vin:
            return deal?.vehicle?.vin ? deal?.vehicle?.vin.substring(deal?.vehicle?.vin.length - 8) : testing ? 'HS019532' : '';
        case FieldTypes.vehicleYear:
            return deal?.vehicle?.year ? deal?.vehicle?.year : testing ? new Date().getFullYear() : '';
        case FieldTypes.vehicleMake:
            return deal?.vehicle?.make ? deal?.vehicle?.make : testing ? 'Ford' : '';
        case FieldTypes.vehicleModel:
            return deal?.vehicle?.model ? deal?.vehicle?.model : testing ? 'F-150' : '';
        case FieldTypes.vehicleTrim:
            return deal?.vehicle?.trim ? deal?.vehicle?.trim : testing ? 'XL' : '';
        case FieldTypes.tradeInVehicleYear:
            return deal?.tradeInVehicle?.year ? deal?.tradeInVehicle?.year : testing ? new Date().getFullYear() : '';
        case FieldTypes.tradeInVehicleMake:
            return deal?.tradeInVehicle?.make ? deal?.tradeInVehicle?.make : testing ? 'Ford' : '';
        case FieldTypes.tradeInVehicleModel:
            return deal?.tradeInVehicle?.model ? deal?.tradeInVehicle?.model : testing ? 'F-150' : '';
        case FieldTypes.tradeInVehicleTrim:
            return deal?.tradeInVehicle?.trim ? deal?.tradeInVehicle?.trim : testing ? 'XL' : '';
        case FieldTypes.tradeInVehiclePayoff:
            return deal?.tradeInVehicle?.payoff ? deal?.tradeInVehicle?.payoff : testing ? '1250' : '0';
        case FieldTypes.dealNumber:
            return deal?.dealerDealNumber ? deal?.dealerDealNumber : testing ? '0000' : '';
        case FieldTypes.stockId:
            return deal?.vehicle?.stockNumber ? deal?.vehicle?.stockNumber : testing ? '5454' : '';
        case FieldTypes.mileage:
            return deal?.vehicle?.mileage ? deal?.vehicle.mileage : testing ? 12345 : '';
        case FieldTypes.tradeInVin:
            return deal?.tradeInVehicle.vin ? deal?.tradeInVehicle.vin : testing ? '123456677756test' : '';
        case FieldTypes.jobTitle:
            return deal?.salesperson?.jobTitle ? deal?.salesperson.jobTitle : testing ? 'Job Title Test' : '';
        case FieldTypes.dealerContact:
            return deal?.dealerContact?.legalName
                ? deal.dealerContact.legalName
                : deal?.dealerContact?.firstName
                  ? `${deal.dealerContact.firstName} ${deal?.dealerContact?.lastName}`
                  : testing
                    ? 'Dealer Smith'
                    : '';
        case FieldTypes.dealershipName:
            return deal?.dealershipName ? deal?.dealershipName : testing ? 'Dealership Name' : '';
        case FieldTypes.listOfDocuments:
            //Handled in function
            return deal?.listOfDocuments ? '' : testing ? 'Document Name' : '';
        case FieldTypes.buyerName:
            return deal?.buyer?.legalName
                ? deal.buyer.legalName
                : deal?.buyer?.firstName
                  ? `${deal.buyer.firstName} ${deal?.buyer?.lastName}`
                  : testing
                    ? 'John Doe'
                    : '';
        case FieldTypes.cobuyerName:
            return deal?.coBuyer?.legalName
                ? deal.coBuyer.legalName
                : deal?.coBuyer?.firstName
                  ? `${deal.coBuyer.firstName} ${deal?.coBuyer?.lastName}`
                  : testing
                    ? 'Jane Doe'
                    : '';
        default:
            return '';
    }
};

export const resetPriority = async (pdfTron: WebViewerInstance | any, activeField: any) => {
    const { documentViewer } = pdfTron.Core;
    const annotationManager = documentViewer.getAnnotationManager();

    const activeWidth = activeField.X + activeField.Width;
    const activeHeight = activeField.Y + activeField.Height;

    if (activeField.custom?.name?.includes('DEAL_') || activeField.custom?.name?.startsWith('line_')) {
        await annotationManager.bringToBack(activeField);
    } else {
        annotationManager.getAnnotationsList({ force: true }).forEach(async (annot) => {
            if (annot.custom?.name !== activeField.custom?.name) {
                const annotWidth = annot.X + annot.Width;
                const annotHeight = annot.Y + annot.Height;
                // Check if the two annotations intersect at all
                const intersects = activeWidth >= annot.X && activeField.X <= annotWidth && activeField.Y <= annotHeight && activeHeight >= annot.Y;
                if (intersects) {
                    // Only manipulate the activeField
                    if (
                        (activeField.X > annot.X && activeField.X < annotWidth) ||
                        (activeField.Y > annot.Y && activeField.X < annotHeight) ||
                        annot.custom?.name?.includes('DEAL_') ||
                        annot.custom?.name?.startsWith('line_')
                    ) {
                        await annotationManager.bringToFront(activeField);
                    } else if ((annot.X > activeField.X && annot.X < activeWidth) || (annot.Y > activeField.Y && annot.X < activeHeight)) {
                        await annotationManager.bringToBack(activeField);
                    }
                }
            }
        });
    }
};

// When a user clicks a signature button, we automatically add it to a certain spot on the document
// This function is a precaution in case they do that multiple times. We will move the field down the
// document to allow them to easily see what annotations are on the document. This has limitations of
// course and will begin stacking once they reach the bottom of the document.
export const setAutoPlacedFieldLocation = async (pdfTron: WebViewerInstance | any, activeField: any) => {
    const { documentViewer } = pdfTron.Core;
    const annotationManager = documentViewer.getAnnotationManager();

    const pageHeight = documentViewer.getPageHeight(activeField.PageNumber);
    const numPages = documentViewer.getPageCount();

    const activeWidth = activeField.X + activeField.Width;
    const activeHeight = activeField.Y + activeField.Height;

    // Loop over current annotations and verify there is not another annot in the same spot
    annotationManager.getAnnotationsList({ force: true }).forEach(async (annot) => {
        if (annot.custom?.name !== activeField.custom?.name && annot.PageNumber === activeField.PageNumber) {
            const annotWidth = annot.X + annot.Width;
            const annotHeight = annot.Y + annot.Height;
            // Check if the two annotations intersect at all
            const intersects = activeWidth >= annot.X && activeField.X <= annotWidth && activeField.Y <= annotHeight && activeHeight >= annot.Y;
            // Verify the potential new position is still on the same page
            const onPage = activeField.Y + annot.Height <= pageHeight;

            if (intersects && onPage) {
                activeField.Y += annot.Height;
                // Recursively check the height to verify no fields are in the way
                setAutoPlacedFieldLocation(pdfTron, activeField);
            } else if (!onPage && activeField.PageNumber < numPages) {
                // If field is not on page move it to the next page if possible
                activeField.PageNumber += 1;
                // Provide a little space to be safe
                activeField.Y = 10;
                setAutoPlacedFieldLocation(pdfTron, activeField);
            }
        }
    });
};

function getCreateInnerElement(pdfTron: WebViewerInstance | any, fieldName: string) {
    const { Annotations } = pdfTron.Core;

    return Annotations.TextWidgetAnnotation.prototype.createInnerElement;
}

function getImpactSettings(document?: DdjDocument, impactLineData?: ImpactPrinterFields) {
    return {
        fontSize: impactLineData?.MetaDataPrinter?.FontSize ?? 10.5,
        fontFamily: impactLineData?.MetaDataPrinter?.FontType ?? 'Courier',
        standardLineHeight: impactLineData.MetaDataPrinter.Driver == PddjPrinters.Oki8480fbImpact ? impactLineData?.MetaDataPrinter?.FontSize : 12,
        spacingAdjustment: 6,
        characterAdjustment: 10,
        verticalAdjustment: document && document.offsetY ? document.offsetY : 0,
        horizontalAdjustment: document && document.offsetX ? document.offsetX : 0,
    };
}

function hasSignedField(widgetName: string, signedPdfFields: [{ [key: string]: PdfField }]): boolean {
    let fieldPreviouslySigned = false;

    if (signedPdfFields !== null && signedPdfFields !== undefined && signedPdfFields.some((x) => x)) {
        fieldPreviouslySigned = signedPdfFields.find((x) => Object.keys(x).includes(widgetName)) !== undefined;
    }

    return fieldPreviouslySigned;
}

//For Debugging only.
function getLineOutput(linecount: number, line: DocumentData, area: string) {
    const letlineDetailed: [LineDetail] = [{ Spacing: '4', Content: '-' + area + '-' + linecount.toString() }];
    line.LineData.push(...letlineDetailed);
    return line;
}

function isSignature(type: string): boolean {
    return type && (type.includes('SIGNATURE') || type.includes('INITIALS') || type.includes('LEGAL_NAME'));
}
